import { pub } from './opinion';
/**
 * Regelversion 5: eine Opposition, die selbst handelt, und der Koalitionsausschuss.
 *
 *  - Untersuchungsausschuss: Ein Viertel der Abgeordneten kann ihn erzwingen (Minderheitenrecht).
 *    Anlass sind Affären, IT-Pannen, eine Haushaltssperre oder Krisen. Die Regierung entscheidet,
 *    wie sie damit umgeht. Nach neun Monaten kommt der Abschlussbericht, belastend oder entlastend.
 *  - Antrag, der die Koalition vorführen soll: Die Opposition stellt einen Antrag genau zu dem Thema,
 *    bei dem die Koalitionspartner am weitesten auseinanderliegen.
 *  - Koalitionsausschuss: Die Regierung kann Streit beilegen, zahlt dafür aber einen Preis.
 *
 * Klagen in Karlsruhe gibt es schon seit Version 1 (engine/court.ts).
 * Im Mehrspieler ist das Modul aus, dort ist die Opposition ein Mensch.
 */
import { NEED } from '../content/consts';
import { AXN } from '../content/axes';
import { OUTLETS, PARTIES } from '../content/parties';
import { DM } from '../content/settings';
import { axes } from './axes';
import { rnd } from './rng';
import { PA, PN } from './rules';
import { S, log } from './state';
import { clamp, dateLabel } from './util';
import { since } from './version';

export const UA_MONTHS = 9, UA_QUORUM = Math.ceil(630 / 4), SPLIT_CD = 8, KA_CD = 6;

export type OppMove =
  | { k: 'ua'; t: string; why: string; by: string; heat: number; m: number }
  | { k: 'split'; ax: string; by: string; partner: string; dir: number; m: number };
export type Ua = { t: string; by: string; until: number; risk: number; mode: number };

let pressFn: ((o: string, t: string, tone: number) => void) | null = null;
export function setPress5(fn: (o: string, t: string, tone: number) => void) { pressFn = fn; }
const press = (o: string, t: string, tone: number) => pressFn?.(o, t, tone);

export const opp5On = (): boolean => since(5) && !S.mp;
const oppIds = (): string[] => (S.res?.inP || []).filter((id: string) => id !== 'YOU' && !S.partners.includes(id));
export const oppSeats = (): number => oppIds().reduce((s: number, id: string) => s + (S.res.seats[id] || 0), 0);
const lead = (): string => oppIds().filter((id) => id !== 'HO').sort((a, b) => S.res.seats[b] - S.res.seats[a])[0] || oppIds()[0];

/** Möglicher Anlass für einen Untersuchungsausschuss. */
function uaReason(): { t: string; why: string; heat: number } | null {
  const h = S.decHist || [];
  if (S.scandalsLeg > 0) return { t: 'Affären im Kabinett', why: 'Die Opposition will klären, was die Regierung über die Affären ihrer Minister wusste.', heat: .55 };
  if (h.includes('it_projekt')) return { t: 'Das gescheiterte IT-Großprojekt', why: 'Wer wusste wann von den Kostenexplosionen bei der Software für die Arbeitsagenturen?', heat: .5 };
  if (S.fiscal?.mode === 'sperre') return { t: 'Der verfassungswidrige Haushalt', why: 'Die Opposition wirft der Regierung vor, Karlsruhe sehenden Auges ignoriert zu haben.', heat: .45 };
  if (h.includes('cyber')) return { t: 'Der Cyberangriff auf die Kommunen', why: 'Warum waren die Behörden so schlecht geschützt?', heat: .4 };
  if ((S.crisisHist || []).length) return { t: 'Das Krisenmanagement der Regierung', why: 'Die Opposition will die Entscheidungen der Regierung in der Krise aufarbeiten.', heat: .35 };
  return null;
}

/** Thema, bei dem die Koalitionspartner am weitesten auseinanderliegen. */
export function splitAxis(): { ax: string; partner: string; dir: number; gap: number } | null {
  let best: { ax: string; partner: string; dir: number; gap: number } | null = null;
  for (const p of S.partners) for (const a of axes()) {
    const pp = PARTIES[p]?.pos?.[a] ?? 0, y = S.you[a] ?? 0, gap = Math.abs(pp - y) * (PARTIES[p]?.w?.[a] ?? 1);
    if (gap >= 2 && (!best || gap > best.gap)) best = { ax: a, partner: p, dir: Math.sign(pp - y) || 1, gap };
  }
  return best;
}
/** Oppositionspartei, die dem Partner bei diesem Thema am nächsten steht. */
function splitBy(ax: string, dir: number): string | null {
  const c = oppIds().filter((id) => Math.sign(PARTIES[id]?.pos?.[ax] ?? 0) === dir);
  return c.sort((a, b) => S.res.seats[b] - S.res.seats[a])[0] || null;
}

// ── Monatlich ──

export function oppTick(): boolean {
  if (!opp5On() || S.phase !== 'govern') return false;
  // Laufender Untersuchungsausschuss
  const U: Ua | null = S.ua || null;
  if (U) {
    S.press = clamp((S.press || 0) - (U.mode === 1 ? .35 : .2), -6, 6);
    if (S.m >= U.until) {
      S.ua = null;
      if (rnd() < U.risk) {
        S.basis -= 1.5; if (S.partners.length) S.coal = clamp(S.coal - 6, 0, 100);
        press(OUTLETS[0].n, `Abschlussbericht: Schwere Vorwürfe gegen die Regierung (${U.t})`, -2.5);
        log(`Der Untersuchungsausschuss zu „${U.t}“ legt seinen Bericht vor. Er belastet die Regierung schwer.`, 'bad');
      } else {
        press(OUTLETS[1].n, `Untersuchungsausschuss entlastet die Regierung (${U.t})`, 1);
        log(`Der Untersuchungsausschuss zu „${U.t}“ entlastet die Regierung.`, 'good');
      }
      return true;
    }
  }
  if (S.oppMove || S.dec || S.crisis) return false;
  // Neuer Untersuchungsausschuss
  if (!U && S.m - (S.uaM ?? -99) >= 18 && oppSeats() >= UA_QUORUM) {
    const R = uaReason();
    if (R && rnd() < .035 * DM()) {
      const by = lead(); S.uaM = S.m;
      S.oppMove = { k: 'ua', t: R.t, why: R.why, by, heat: R.heat, m: S.m };
      log(`${PN(by)} setzt einen Untersuchungsausschuss durch: ${R.t}.`, 'bad');
      press(OUTLETS[0].n, `Opposition erzwingt Untersuchungsausschuss: ${R.t}`, -.8);
      return true;
    }
  }
  // Antrag, der die Koalition vorführen soll
  if (S.partners.length && S.m - (S.splitM ?? -99) >= SPLIT_CD) {
    const sp = splitAxis();
    if (sp && rnd() < .05 * DM()) {
      const by = splitBy(sp.ax, sp.dir);
      if (by) {
        S.splitM = S.m;
        S.oppMove = { k: 'split', ax: sp.ax, by, partner: sp.partner, dir: sp.dir, m: S.m };
        log(`${PN(by)} stellt einen Antrag zum Thema ${AXN[sp.ax]}, der genau auf den Streit mit ${PA(sp.partner)} zielt.`, 'bad');
        press(OUTLETS[0].n, `${PA(by)} will die Koalition beim Thema ${AXN[sp.ax]} vorführen`, -.3);
        return true;
      }
    }
  }
  return false;
}

// ── Reaktion der Regierung ──

export const UA_OPTS: [string, string][] = [
  ['Voll kooperieren', 'Alle Akten offenlegen. Kurzfristig unangenehm, das Risiko eines belastenden Berichts sinkt deutlich.'],
  ['Akten zögerlich herausgeben', 'Kein Ärger heute, aber der Ausschuss bleibt länger in den Schlagzeilen und das Risiko steigt.'],
  ['Gegenangriff: politisch motiviert', 'Mobilisiert die eigene Basis und spaltet. Das Risiko bleibt hoch.'],
];
export const SPLIT_OPTS: [string, string][] = [
  ['Geschlossen ablehnen', 'Koalitionsdisziplin: Der Partner muss gegen seine eigene Überzeugung stimmen.'],
  ['Abstimmung freigeben', 'Der Partner stimmt mit der Opposition. Souverän, aber die Koalition wirkt uneins.'],
  ['Eigenen Gegenantrag stellen', 'Ein Kompromiss rettet das Gesicht. Kostet einen Termin für Öffentlichkeitsarbeit.'],
];

export function oppMove(i: number) {
  const M: OppMove | null = S.oppMove || null; if (!M || !opp5On()) return;
  if (M.k === 'ua') {
    const o = clamp(Math.round(i), 0, 2);
    const risk = M.heat * [.5, .95, .85][o];
    S.ua = { t: M.t, by: M.by, until: S.m + UA_MONTHS, risk: Math.round(risk * 100) / 100, mode: o };
    if (o === 0) press(OUTLETS[1].n, `Regierung sagt volle Aufklärung zu: ${M.t}`, -.4);
    if (o === 2) { S.basis += .5; (S as any).polar = ((S as any).polar || 0) + 5; press(OUTLETS[3].n, `Regierung wirft ${PA(M.by)} Schmutzkampagne vor`, -.3); }
    log(`Untersuchungsausschuss „${M.t}“: ${UA_OPTS[o][0]}. Der Bericht kommt voraussichtlich ${dateLabel(S.m + UA_MONTHS)}.`);
  } else {
    const o = clamp(Math.round(i), 0, 2);
    if (o === 2 && !((S.com?.pts ?? 0) >= 1)) return;
    const pv = pub(M.ax), popular = Math.sign(pv) === M.dir && Math.abs(pv) >= .5;
    if (o === 0) {
      S.coal = clamp(S.coal - 4, 0, 100);
      press(OUTLETS[2].n, popular ? `Koalition stimmt gegen populären Antrag zum Thema ${AXN[M.ax]}` : `Koalition steht geschlossen beim Thema ${AXN[M.ax]}`, popular ? -1 : .3);
      log(`Ihr lehnt den Antrag geschlossen ab. ${PN(M.partner)} stimmt zähneknirschend gegen die eigene Überzeugung.`, popular ? 'bad' : '');
    }
    if (o === 1) {
      S.coal = clamp(S.coal - 7, 0, 100); S.basis -= .3;
      press(OUTLETS[0].n, `${PA(M.partner)} stimmt mit ${PA(M.by)}: Koalition uneins beim Thema ${AXN[M.ax]}`, .2);
      log(`Ihr gebt die Abstimmung frei. ${PN(M.partner)} stimmt mit der Opposition.`, 'bad');
    }
    if (o === 2) {
      S.com.pts -= 1; S.coal = clamp(S.coal - 1, 0, 100);
      press(OUTLETS[1].n, `Koalition kontert mit eigenem Antrag zum Thema ${AXN[M.ax]}`, .2);
      log('Euer Gegenantrag findet eine Mehrheit, der Antrag der Opposition ist vom Tisch.', 'good');
    }
  }
  S.oppMove = null;
}

// ── Koalitionsausschuss ──

export const kaReady = (): boolean => opp5On() && S.phase === 'govern' && S.partners.length > 0 && S.m - (S.kaM ?? -99) >= KA_CD;
/** Thema, bei dem euer Koalitionsvertrag am weitesten vom Partner entfernt ist. */
export function kaAxis(): { ax: string; partner: string } | null {
  if (!S.agree) return null;
  let best: { ax: string; partner: string; g: number } | null = null;
  for (const p of S.partners) for (const a of axes()) {
    const g = Math.abs((PARTIES[p]?.pos?.[a] ?? 0) - (S.agree[a] ?? 0));
    if (g >= 1 && (!best || g > best.g)) best = { ax: a, partner: p, g };
  }
  return best;
}
export function coalCommittee(i: number) {
  if (!kaReady()) return;
  const o = clamp(Math.round(i), 0, 2);
  if (o === 0) {
    const k = kaAxis(); if (!k) return;
    const pp = PARTIES[k.partner].pos[k.ax] ?? 0, v = S.agree[k.ax] ?? 0;
    S.agree[k.ax] = v + Math.sign(pp - v);
    S.coal = clamp(S.coal + 15, 0, 100); S.basis -= .4;
    log(`Koalitionsausschuss: Ihr kommt ${PN(k.partner)} beim Thema ${AXN[k.ax]} entgegen. Der Koalitionsvertrag wird angepasst.`);
    press(OUTLETS[0].n, `Koalitionsausschuss: Einigung beim Thema ${AXN[k.ax]}`, .3);
  }
  if (o === 1) {
    S.lawB -= 1.5; S.coal = clamp(S.coal + 12, 0, 100);
    log('Koalitionsausschuss: Die Ressorts des Partners bekommen 1,5 Mrd € im Jahr zusätzlich.');
    press(OUTLETS[1].n, 'Koalitionsausschuss erkauft Frieden mit Milliarden', -.2);
  }
  if (o === 2) { S.coal = clamp(S.coal + 5, 0, 100); log('Koalitionsausschuss: ein langer Abend, viele Worte, wenig Ergebnis.'); }
  S.kaM = S.m;
}
export { NEED };
